import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { canSeeDashboardModule, resolveDashboardHeroSideCard } from '../../../constants/adminModules';
import AdminDashboardRail from './AdminDashboardRail';
import AdminPageShell from '../AdminPageShell';
import Module10TrendChart from './Module10TrendChart';
import { formatMoney } from '../../../utils/currency';

const QUICK_LINKS = [
  { id: 'inscriptions', label: 'Inscriptions', icon: 'assignment', path: '/admin/inscriptions', tone: 'violet' },
  { id: 'academique', label: 'Académique', icon: 'menu_book', path: '/admin/academique', tone: 'white' },
  { id: 'apprenants', label: 'Apprenants', icon: 'group', path: '/admin/apprenants', tone: 'white' },
  { id: 'formations', label: 'Formations', icon: 'school', path: '/admin/formations', tone: 'white' },
  { id: 'paiements', label: 'Paiements', icon: 'account_balance', path: '/admin/paiements', tone: 'white' },
  { id: 'tarifs', label: 'Tarifs', icon: 'payments', path: '/admin/tarifs', tone: 'white' },
  { id: 'settings', label: 'Devise', icon: 'settings_suggest', path: '/admin/settings', tone: 'white' },
] as const;

type DashboardUser = {
  role?: string;
  adminModules?: string[];
};

type Props = {
  user?: DashboardUser;
  userName?: string;
  data: {
    inscriptionsTotal: number;
    apprenantsTotal: number;
    cohortesOuvertes: number;
    cohortesPleines?: number;
    cohortesPresquePleines?: number;
    alertesCapacite?: {
      pleines?: Array<{ code?: string; libelle?: string; filiereLibelle?: string; inscrits?: number; capaciteEffective?: number }>;
      presquePleines?: Array<{ code?: string; libelle?: string }>;
    };
    inscriptionsEnAttentePaiement: number;
    inscriptionsPartiellementPayees?: number;
    inscriptionsConfirmees: number;
    montantPayeCdf: number;
    montantTotalCdf: number;
    paiements?: {
      recettesJournalieres?: number;
      recettesMensuelles?: number;
      bonusDistribuesMois?: number;
      montantImpayeTotal?: number;
      paiementsEnAttente?: number;
      projections?: { projectionFinDeMois?: number; projectionTrimestre?: number };
    };
    module10?: {
      academique?: {
        periode?: string;
        nombreApprenants?: number;
        tauxReussitePct?: number | null;
        inscriptionsReussies?: number;
        tauxAbandonPct?: number | null;
        inscriptionsAbandonnees?: number;
        classementGeneralTotal?: number;
        classementGeneral?: {
          rang: number | null;
          nomComplet: string;
          matricule: string;
          scoreGlobal: number;
          estShortlist?: boolean;
        }[];
        classementShortlist?: number;
        tailleShortlist?: number;
        meilleursEtudiants?: { rang: number | null; nomComplet: string; scoreGlobal: number; matricule?: string }[];
      };
      financier?: {
        recettesJournalieres?: number;
        recettesMensuelles?: number;
        bonusDistribuesMois?: number;
        paiementsEnAttente?: number;
        montantImpayeTotal?: number;
        tendancesRecettes14j?: { label: string; montant: number }[];
        projections?: { projectionFinDeMois?: number; projectionTrimestre?: number; moyenneJournaliereSur30j?: number };
      };
      rh?: {
        heuresPresteesMois?: number;
        remunerationsDuCdf?: number;
        remunerationsPayeesCdf?: number;
        tauxSatisfactionPedagogiquePct?: number | null;
        nombreEvaluations?: number;
        tendancesHeures8s?: { label: string; heures: number }[];
        performancesFormateurs?: { nom: string; noteMoyenne: number; nombreEvaluations: number }[];
        meilleursReferents?: { nom: string; codeParrainage: string; filleuls: number }[];
      };
      logistique?: {
        totalArticles?: number;
        equipementsDisponibles?: number;
        equipementsEnPanne?: number;
        pannesOuvertes?: number;
        besoinsRenouvellement?: number;
        etatParStatut?: { label: string; count: number }[];
      };
    };
  } | null;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
};

function ModuleGate({
  user,
  moduleKey,
  children,
}: {
  user?: DashboardUser;
  moduleKey: string;
  children: ReactNode;
}) {
  if (!canSeeDashboardModule(user, moduleKey)) return null;
  return <>{children}</>;
}

function DashboardKpiGroup({
  user,
  moduleKeys,
  label,
  children,
}: {
  user?: DashboardUser;
  moduleKeys: string[];
  label: string;
  children: ReactNode;
}) {
  const visible = moduleKeys.some((key) => canSeeDashboardModule(user, key));
  if (!visible) return null;
  return <KpiDomainGroup label={label}>{children}</KpiDomainGroup>;
}

function DashboardSectionGate({
  user,
  moduleKey,
  title,
  subtitle,
  icon,
  linkTo,
  children,
}: {
  user?: DashboardUser;
  moduleKey: string;
  title: string;
  subtitle?: string;
  icon: string;
  linkTo?: string;
  children: ReactNode;
}) {
  if (!canSeeDashboardModule(user, moduleKey)) return null;
  return (
    <DashboardSection title={title} subtitle={subtitle} icon={icon} linkTo={linkTo}>
      {children}
    </DashboardSection>
  );
}

function DashboardSection({
  title,
  subtitle,
  icon,
  linkTo,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: string;
  linkTo?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-outline-variant/20 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LucideSymbolIcon name={icon} tone="inherit" size={22} />
          </span>
          <div>
            <h3 className="text-lg font-black text-slate-900">{title}</h3>
            {subtitle ? <p className="mt-1 text-xs text-on-surface-variant">{subtitle}</p> : null}
          </div>
        </div>
        {linkTo ? (
          <Link to={linkTo} className="text-xs font-bold text-primary hover:underline">
            Voir le module →
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function TopList({
  items,
  emptyLabel,
}: {
  items: { key: string; title: string; meta: string; value?: string }[];
  emptyLabel: string;
}) {
  if (items.length === 0) {
    return <p className="text-xs text-on-surface-variant">{emptyLabel}</p>;
  }
  return (
    <ol className="space-y-2">
      {items.map((item) => (
        <li
          key={item.key}
          className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/15 bg-slate-50/80 px-3 py-2 text-sm"
        >
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{item.title}</p>
            <p className="text-[11px] text-on-surface-variant">{item.meta}</p>
          </div>
          {item.value ? <span className="shrink-0 font-black text-primary">{item.value}</span> : null}
        </li>
      ))}
    </ol>
  );
}

function KpiCard({
  title,
  value,
  helper,
  icon,
  highlight,
  compact,
}: {
  title: string;
  value: string;
  helper: string;
  icon: string;
  highlight?: boolean;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="min-w-0 overflow-hidden rounded-xl border border-outline-variant/15 bg-surface-container-low p-3">
        <div className="flex items-start gap-2.5">
          <span
            className={[
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              highlight ? 'bg-primary/10 text-primary' : 'bg-white text-on-surface-variant ring-1 ring-outline-variant/10',
            ].join(' ')}
          >
            <LucideSymbolIcon name={icon} tone="inherit" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] font-semibold text-on-surface-variant">{title}</p>
            <p
              className="mt-0.5 break-words text-base font-black leading-tight text-primary tabular-nums [overflow-wrap:anywhere]"
              title={value}
            >
              {value}
            </p>
            <p className="mt-1 break-words text-[10px] leading-snug text-on-surface-variant [overflow-wrap:anywhere]">{helper}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group min-w-0 overflow-hidden rounded-xl bg-white p-4 shadow-sm transition-all duration-300 hover:shadow-lg">
      <div
        className={[
          'mb-4 flex h-14 w-14 items-center justify-center rounded-lg',
          highlight ? 'bg-primary/10 text-primary' : 'bg-surface-container text-on-surface-variant',
        ].join(' ')}
      >
        <LucideSymbolIcon name={icon} tone="inherit" size={26} />
      </div>
      <h4 className="mb-1 truncate text-base font-bold">{title}</h4>
      <p
        className="break-words text-xl font-black text-primary tabular-nums [overflow-wrap:anywhere] sm:text-2xl"
        title={value}
      >
        {value}
      </p>
      <p className="mt-2 break-words text-[11px] text-on-surface-variant [overflow-wrap:anywhere]">{helper}</p>
    </div>
  );
}

function KpiDomainGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">{label}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 [&>*]:min-w-0">{children}</div>
    </div>
  );
}

type ClassementGeneralRows = NonNullable<
  NonNullable<NonNullable<Props['data']>['module10']>['academique']
>['classementGeneral'];

function ClassementGeneralTable({
  rows,
  total,
}: {
  rows: ClassementGeneralRows;
  total?: number;
}) {
  if (!rows?.length) {
    return (
      <p className="text-xs text-on-surface-variant">
        Aucun classement publié — lancez une délibération académique pour générer le classement général.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/15">
      <div className="max-h-64 overflow-y-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
            <tr>
              <th className="px-3 py-2">Rang</th>
              <th className="px-3 py-2">Apprenant</th>
              <th className="px-3 py-2">Matricule</th>
              <th className="px-3 py-2 text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.rang}-${row.matricule}`} className="border-t border-outline-variant/10">
                <td className="px-3 py-2 font-black text-primary">#{row.rang ?? '—'}</td>
                <td className="px-3 py-2 font-semibold text-slate-900">
                  {row.nomComplet}
                  {row.estShortlist ? (
                    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                      Shortlist
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-on-surface-variant">{row.matricule}</td>
                <td className="px-3 py-2 text-right font-black">{row.scoreGlobal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(total ?? 0) > (rows?.length ?? 0) ? (
        <p className="border-t border-outline-variant/10 bg-slate-50 px-3 py-2 text-[11px] text-on-surface-variant">
          Affichage des {rows.length} premiers sur {total} classés.
        </p>
      ) : null}
    </div>
  );
}

function LogisticsStatusBars({
  items,
}: {
  items: { label: string; count: number }[];
}) {
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="mt-4 space-y-2">
      {items.map((item) => (
        <div key={item.label}>
          <div className="mb-1 flex justify-between text-xs">
            <span className="font-semibold text-slate-800">{item.label}</span>
            <span className="font-black text-primary">{item.count}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-primary/80"
              style={{ width: `${Math.max(4, (item.count / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function exportModule10Csv(module10: NonNullable<Props['data']>['module10']) {
  if (!module10) return;
  const escape = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines: string[] = [];
  lines.push(['Section', 'Indicateur', 'Valeur'].map(escape).join(','));
  const a = module10.academique;
  if (a) {
    lines.push(['Académique', 'Apprenants', a.nombreApprenants ?? 0].map(escape).join(','));
    lines.push(['Académique', 'Taux réussite %', a.tauxReussitePct ?? ''].map(escape).join(','));
    lines.push(['Académique', 'Classement général', a.classementGeneralTotal ?? 0].map(escape).join(','));
    lines.push(['Académique', 'Taux abandon %', a.tauxAbandonPct ?? ''].map(escape).join(','));
  }
  const f = module10.financier;
  if (f) {
    lines.push(['Financier', 'Recettes jour', f.recettesJournalieres ?? 0].map(escape).join(','));
    lines.push(['Financier', 'Recettes mois', f.recettesMensuelles ?? 0].map(escape).join(','));
    lines.push(['Financier', 'Bonus mois', f.bonusDistribuesMois ?? 0].map(escape).join(','));
  }
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tableaux-de-bord-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function AcademiaDashboardPanel({
  user,
  userName = 'Administrateur',
  data,
  isLoading,
  isError,
  onRefresh,
}: Props) {
  useRegisterPortalRefresh(onRefresh);

  const visibleQuickLinks = QUICK_LINKS.filter((item) => canSeeDashboardModule(user, item.id));
  const showCohortAlert = canSeeDashboardModule(user, 'formations');
  const showConsolidated =
    canSeeDashboardModule(user, 'inscriptions') ||
    canSeeDashboardModule(user, 'apprenants') ||
    canSeeDashboardModule(user, 'formations') ||
    canSeeDashboardModule(user, 'paiements') ||
    canSeeDashboardModule(user, 'parrainage') ||
    canSeeDashboardModule(user, 'academique') ||
    canSeeDashboardModule(user, 'excellence') ||
    canSeeDashboardModule(user, 'formateurs') ||
    canSeeDashboardModule(user, 'logistique');
  const showFallbackKpis =
    canSeeDashboardModule(user, 'inscriptions') ||
    canSeeDashboardModule(user, 'apprenants') ||
    canSeeDashboardModule(user, 'formations') ||
    canSeeDashboardModule(user, 'paiements');

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-xl bg-white p-12 shadow-sm">
        <p className="text-sm text-on-surface-variant">Chargement du tableau de bord…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl bg-white p-8 shadow-sm">
        <p className="text-sm text-on-surface-variant">Impossible de charger les statistiques.</p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const firstName = userName.split(/\s+/)[0] || userName;
  const m10 = data.module10;
  const fin = m10?.financier ?? data.paiements;
  const heroSideCard = resolveDashboardHeroSideCard(user, data);

  return (
    <div className="flex flex-col gap-8 xl:flex-row">
      <div className="min-w-0 flex-1 space-y-8">
        <AdminPageShell
          title="Pilotez les inscriptions et les paiements"
          description={
            m10
              ? 'Vue consolidée : inscriptions, finances, académique, RH et logistique — indicateurs synchronisés en temps réel.'
              : 'Suivez les cohortes ouvertes, relancez les apprenants en attente et consultez les encaissements CDF.'
          }
          sideCard={heroSideCard ?? null}
          topSlot={
            <h2 className="text-2xl font-bold tracking-tight text-on-surface">Bonjour, {firstName}</h2>
          }
        >
        {showCohortAlert &&
        ((data.cohortesPleines ?? 0) > 0 || (data.cohortesPresquePleines ?? 0) > 0) ? (
          <section className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm font-bold text-amber-950">
              {(data.cohortesPleines ?? 0) > 0
                ? `${data.cohortesPleines} cohorte(s) complète(s) — créneaux saturés, plus de places disponibles.`
                : null}
              {(data.cohortesPresquePleines ?? 0) > 0
                ? ` ${data.cohortesPresquePleines} cohorte(s) presque pleine(s).`
                : null}
            </p>
            {(data.alertesCapacite?.pleines?.length ?? 0) > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-amber-900">
                {data.alertesCapacite!.pleines!.slice(0, 5).map((c) => (
                  <li key={c.code}>
                    <strong>{c.code}</strong> — {c.filiereLibelle} ({c.inscrits}/{c.capaciteEffective} inscrits)
                  </li>
                ))}
              </ul>
            ) : null}
            <Link to="/admin/formations" className="mt-2 inline-block text-xs font-bold text-primary">
              Gérer les cohortes →
            </Link>
          </section>
        ) : null}

        {visibleQuickLinks.length > 0 ? (
        <section>
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-bold">Accès rapide</h3>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {visibleQuickLinks.map((item, index) => (
              <Link
                key={item.id}
                to={item.path}
                className={[
                  'flex min-w-[100px] flex-col items-center gap-3 rounded-xl p-4 shadow-sm transition-all',
                  index === 0
                    ? 'bg-primary text-white shadow-md hover:scale-105'
                    : 'bg-white text-on-surface-variant hover:shadow-md',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex h-12 w-12 items-center justify-center rounded-lg',
                    index === 0 ? 'bg-white/20' : 'bg-surface-container',
                  ].join(' ')}
                >
                  <LucideSymbolIcon
                    name={item.icon}
                    tone="inherit"
                    className={index === 0 ? 'text-white' : undefined}
                    size={24}
                  />
                </div>
                <span className="text-sm font-bold">{item.label}</span>
              </Link>
            ))}
          </div>
        </section>
        ) : null}

        {m10 && showConsolidated ? (
          <section className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-xl font-bold">Indicateurs consolidés</h3>
              <button
                type="button"
                onClick={() => exportModule10Csv(m10)}
                className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-white px-3 py-2 text-xs font-bold text-primary hover:bg-primary/5"
              >
                <LucideSymbolIcon name="download" tone="inherit" size={16} />
                Exporter CSV
              </button>
            </div>

            <div className="space-y-6 rounded-2xl border border-outline-variant/20 bg-white p-5 shadow-sm sm:p-6">
              <DashboardKpiGroup
                user={user}
                moduleKeys={['inscriptions', 'apprenants', 'formations']}
                label="Inscriptions & cohortes"
              >
                <ModuleGate user={user} moduleKey="inscriptions">
                  <KpiCard compact title="Inscriptions" value={String(data.inscriptionsTotal)} helper={`${data.inscriptionsConfirmees} confirmées`} icon="assignment" highlight />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="apprenants">
                  <KpiCard compact title="Apprenants" value={String(m10.academique?.nombreApprenants ?? data.apprenantsTotal)} helper="Au moins un versement" icon="group" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="formations">
                  <KpiCard compact title="Cohortes ouvertes" value={String(data.cohortesOuvertes)} helper="Statut OUVERTE" icon="school" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="inscriptions">
                  <KpiCard compact title="En attente" value={String(data.inscriptionsEnAttentePaiement)} helper="Paiement à relancer" icon="warning" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="inscriptions">
                  <KpiCard compact title="Acomptes" value={String(data.inscriptionsPartiellementPayees ?? 0)} helper="Partiellement payées" icon="payments" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="inscriptions">
                  <KpiCard compact title="Confirmées" value={String(data.inscriptionsConfirmees)} helper="Inscriptions validées" icon="check_circle" />
                </ModuleGate>
              </DashboardKpiGroup>

              <DashboardKpiGroup user={user} moduleKeys={['academique', 'excellence']} label="Académique">
                <ModuleGate user={user} moduleKey="academique">
                  <KpiCard
                    compact
                    title="Taux de réussite"
                    value={m10.academique?.tauxReussitePct != null ? `${m10.academique.tauxReussitePct} %` : '—'}
                    helper={`${m10.academique?.inscriptionsReussies ?? 0} parcours aboutis`}
                    icon="check_circle"
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="excellence">
                  <KpiCard
                    compact
                    title="Classement"
                    value={String(m10.academique?.classementGeneralTotal ?? 0)}
                    helper={`Shortlist ${m10.academique?.classementShortlist ?? 0} / ${m10.academique?.tailleShortlist ?? '—'}`}
                    icon="emoji_events"
                    highlight
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="academique">
                  <KpiCard
                    compact
                    title="Taux d'abandon"
                    value={m10.academique?.tauxAbandonPct != null ? `${m10.academique.tauxAbandonPct} %` : '—'}
                    helper={`${m10.academique?.inscriptionsAbandonnees ?? 0} désistement(s)`}
                    icon="person_off"
                  />
                </ModuleGate>
              </DashboardKpiGroup>

              <DashboardKpiGroup user={user} moduleKeys={['paiements', 'parrainage']} label="Finances">
                <ModuleGate user={user} moduleKey="paiements">
                  <KpiCard compact title="Encaissé" value={formatMoney(data.montantPayeCdf, 'CDF')} helper={`Sur ${formatMoney(data.montantTotalCdf, 'CDF')} facturé`} icon="account_balance_wallet" highlight />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="paiements">
                  <KpiCard compact title="Recettes du jour" value={formatMoney(fin?.recettesJournalieres ?? 0, 'CDF')} helper="Transactions validées" icon="payments" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="paiements">
                  <KpiCard compact title="Recettes du mois" value={formatMoney(fin?.recettesMensuelles ?? 0, 'CDF')} helper="Mois en cours" icon="receipt_long" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="parrainage">
                  <KpiCard compact title="Bonus parrainage" value={formatMoney(fin?.bonusDistribuesMois ?? 0, 'CDF')} helper="Mois en cours" icon="card_membership" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="paiements">
                  <KpiCard
                    compact
                    title="Impayés"
                    value={formatMoney(fin?.montantImpayeTotal ?? 0, 'CDF')}
                    helper={`${fin?.paiementsEnAttente ?? data.inscriptionsEnAttentePaiement} dossier(s)`}
                    icon="schedule"
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="paiements">
                  <KpiCard
                    compact
                    title="Projection fin de mois"
                    value={formatMoney(fin?.projections?.projectionFinDeMois ?? 0, 'CDF')}
                    helper={`Trimestre : ${formatMoney(fin?.projections?.projectionTrimestre ?? 0, 'CDF')}`}
                    icon="auto_graph"
                  />
                </ModuleGate>
              </DashboardKpiGroup>

              <DashboardKpiGroup user={user} moduleKeys={['formateurs']} label="RH & formateurs">
                <ModuleGate user={user} moduleKey="formateurs">
                  <KpiCard compact title="Heures prestées" value={`${m10.rh?.heuresPresteesMois ?? 0} h`} helper="Mois en cours" icon="schedule" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="formateurs">
                  <KpiCard
                    compact
                    title="Rémunérations dues"
                    value={formatMoney(m10.rh?.remunerationsDuCdf ?? 0, 'CDF')}
                    helper={`Payées : ${formatMoney(m10.rh?.remunerationsPayeesCdf ?? 0, 'CDF')}`}
                    icon="account_balance_wallet"
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="formateurs">
                  <KpiCard
                    compact
                    title="Satisfaction"
                    value={m10.rh?.tauxSatisfactionPedagogiquePct != null ? `${m10.rh.tauxSatisfactionPedagogiquePct} %` : '—'}
                    helper="Évaluations formateurs"
                    icon="stars"
                    highlight
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="formateurs">
                  <KpiCard compact title="Évaluations" value={String(m10.rh?.nombreEvaluations ?? 0)} helper="Notes enregistrées" icon="bar_chart" />
                </ModuleGate>
              </DashboardKpiGroup>

              <DashboardKpiGroup user={user} moduleKeys={['logistique']} label="Logistique">
                <ModuleGate user={user} moduleKey="logistique">
                  <KpiCard compact title="Articles" value={String(m10.logistique?.totalArticles ?? 0)} helper="Patrimoine total" icon="inventory_2" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="logistique">
                  <KpiCard compact title="Disponibles" value={String(m10.logistique?.equipementsDisponibles ?? 0)} helper="En stock" icon="check_circle" />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="logistique">
                  <KpiCard
                    compact
                    title="En panne"
                    value={String(m10.logistique?.equipementsEnPanne ?? 0)}
                    helper={`${m10.logistique?.pannesOuvertes ?? 0} signalement(s)`}
                    icon="warning"
                  />
                </ModuleGate>
                <ModuleGate user={user} moduleKey="logistique">
                  <KpiCard compact title="À renouveler" value={String(m10.logistique?.besoinsRenouvellement ?? 0)} helper="Maintenance & ruptures" icon="build" />
                </ModuleGate>
              </DashboardKpiGroup>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <DashboardSectionGate
                user={user}
                moduleKey="academique"
                title="Académique — détail"
                subtitle={
                  m10.academique?.periode
                    ? `Période excellence ${m10.academique.periode}`
                    : 'Classement et parcours apprenants'
                }
                icon="school"
                linkTo="/admin/academique"
              >
                <div className="space-y-4">
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                      Meilleurs étudiants
                    </p>
                    <TopList
                      items={(m10.academique?.meilleursEtudiants ?? []).map((s) => ({
                        key: `${s.rang}-${s.nomComplet}`,
                        title: s.nomComplet,
                        meta: `Rang #${s.rang ?? '—'}${s.matricule ? ` · ${s.matricule}` : ''}`,
                        value: String(s.scoreGlobal),
                      }))}
                      emptyLabel="Aucun classement publié pour la période."
                    />
                  </div>
                  {canSeeDashboardModule(user, 'excellence') ? (
                    <div>
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                          Classement général
                        </p>
                        <Link to="/admin/excellence" className="text-xs font-bold text-primary hover:underline">
                          Voir tout →
                        </Link>
                      </div>
                      <ClassementGeneralTable
                        rows={m10.academique?.classementGeneral}
                        total={m10.academique?.classementGeneralTotal}
                      />
                    </div>
                  ) : null}
                </div>
              </DashboardSectionGate>

              <DashboardSectionGate
                user={user}
                moduleKey="logistique"
                title="Logistique — patrimoine"
                subtitle="Répartition par statut"
                icon="inventory_2"
                linkTo="/admin/logistique"
              >
                {(m10.logistique?.etatParStatut ?? []).length > 0 ? (
                  <LogisticsStatusBars items={m10.logistique!.etatParStatut!} />
                ) : (
                  <p className="text-xs text-on-surface-variant">Aucun équipement inventorié pour le moment.</p>
                )}
              </DashboardSectionGate>

              {canSeeDashboardModule(user, 'paiements') ? (
              <div className="xl:col-span-2">
                <DashboardSection
                  title="Finances — tendances"
                  subtitle="Évolution des recettes sur 14 jours"
                  icon="payments"
                  linkTo="/admin/paiements"
                >
                  <Module10TrendChart
                    title="Recettes (14 jours)"
                    latestLabel="Hier / aujourd'hui"
                    points={(m10.financier?.tendancesRecettes14j ?? []).map((p) => ({
                      label: p.label,
                      value: p.montant,
                    }))}
                    valueSuffix=" CDF"
                    emptyLabel="Aucune recette validée sur les 14 derniers jours."
                  />
                  {m10.financier?.projections?.moyenneJournaliereSur30j != null ? (
                    <p className="mt-3 text-xs text-on-surface-variant">
                      Moyenne journalière sur 30 j :{' '}
                      {formatMoney(m10.financier.projections.moyenneJournaliereSur30j, 'CDF')}
                    </p>
                  ) : null}
                </DashboardSection>
              </div>
              ) : null}

              {canSeeDashboardModule(user, 'formateurs') ? (
              <div className="xl:col-span-2">
                <DashboardSection
                  title="RH — performance"
                  subtitle="Évolution des heures prestées sur 8 semaines"
                  icon="cast_for_education"
                  linkTo="/admin/formateurs"
                >
                  <Module10TrendChart
                    title="Heures prestées (8 semaines)"
                    latestLabel="Semaine en cours"
                    points={(m10.rh?.tendancesHeures8s ?? []).map((p) => ({
                      label: p.label,
                      value: p.heures,
                    }))}
                    valueSuffix=" h"
                    emptyLabel="Aucune prestation enregistrée sur les 8 dernières semaines."
                  />
                </DashboardSection>
              </div>
              ) : null}
            </div>
          </section>
        ) : showFallbackKpis ? (
          <section>
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-bold">Indicateurs clés</h3>
              {canSeeDashboardModule(user, 'apprenants') ? (
                <Link to="/admin/apprenants" className="flex items-center gap-1 text-sm font-bold text-primary">
                  Voir tout
                  <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                </Link>
              ) : null}
            </div>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
              <ModuleGate user={user} moduleKey="inscriptions">
                <KpiCard
                  title="Inscriptions"
                  value={String(data.inscriptionsTotal)}
                  helper={`${data.inscriptionsConfirmees} confirmées avec paiement`}
                  icon="assignment"
                  highlight
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="apprenants">
                <KpiCard
                  title="Apprenants"
                  value={String(data.apprenantsTotal)}
                  helper="Acompte ou paiement complet"
                  icon="group"
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="inscriptions">
                <KpiCard
                  title="Acomptes versés"
                  value={String(data.inscriptionsPartiellementPayees ?? 0)}
                  helper="Inscriptions partiellement payées"
                  icon="payments"
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="formations">
                <KpiCard
                  title="Cohortes ouvertes"
                  value={String(data.cohortesOuvertes)}
                  helper="Statut OUVERTE"
                  icon="school"
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="inscriptions">
                <KpiCard
                  title="En attente"
                  value={String(data.inscriptionsEnAttentePaiement)}
                  helper="Paiement à relancer"
                  icon="warning"
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="inscriptions">
                <KpiCard
                  title="Confirmées"
                  value={String(data.inscriptionsConfirmees)}
                  helper="Inscriptions validées"
                  icon="check_circle"
                />
              </ModuleGate>
              <ModuleGate user={user} moduleKey="paiements">
                <KpiCard
                  title="Facturé"
                  value={formatMoney(data.montantTotalCdf, 'CDF')}
                  helper="Montant total"
                  icon="receipt_long"
                />
              </ModuleGate>
              {data.paiements ? (
                <>
                  <ModuleGate user={user} moduleKey="paiements">
                    <KpiCard
                      title="Recettes du jour"
                      value={formatMoney(data.paiements.recettesJournalieres ?? 0, 'CDF')}
                      helper="Transactions formations validées"
                      icon="payments"
                    />
                  </ModuleGate>
                  <ModuleGate user={user} moduleKey="paiements">
                    <KpiCard
                      title="Impayés"
                      value={formatMoney(data.paiements.montantImpayeTotal ?? 0, 'CDF')}
                      helper={`${data.paiements.paiementsEnAttente ?? 0} dossier(s) à relancer`}
                      icon="warning"
                    />
                  </ModuleGate>
                  <ModuleGate user={user} moduleKey="paiements">
                    <KpiCard
                      title="Projection fin de mois"
                      value={formatMoney(data.paiements.projections?.projectionFinDeMois ?? 0, 'CDF')}
                      helper={`Mois en cours : ${formatMoney(data.paiements.recettesMensuelles ?? 0, 'CDF')}`}
                      icon="auto_graph"
                    />
                  </ModuleGate>
                </>
              ) : null}
            </div>
          </section>
        ) : null}
        </AdminPageShell>
      </div>

      <AdminDashboardRail data={data} user={user} />
    </div>
  );
}
